import type { Element } from './elements.js';
import type { SectId, WeaponType } from './sects.js';

export type SkillCategory =
  | 'basic'
  | 'single'
  | 'area'
  | 'projectile'
  | 'buff'
  | 'debuff'
  | 'heal'
  | 'defensive'
  | 'movement'
  | 'control'
  | 'summon'
  | 'ultimate'
  | 'passive';

/** 'physical' scales from Attack, 'qi' from Qi Attack. Both may carry an element. */
export type DamageType = 'physical' | 'qi';

export type AreaShape =
  | { kind: 'single' }
  /** Cone in front of the caster. */
  | { kind: 'arc'; radius: number; halfAngleDeg: number }
  /** Circle centred on the caster. */
  | { kind: 'circle'; radius: number }
  /** Travelling projectile with a rectangular footprint. */
  | { kind: 'line'; length: number; width: number };

export type StatusId = 'slow' | 'stun';

export interface StatusApplication {
  status: StatusId;
  chance: number;
  durationMs: number;
  /** Meaning depends on the status: for 'slow' it is the fraction of speed removed. */
  magnitude: number;
}

/** A timed damage event inside a skill. */
export interface SkillHit {
  /** Milliseconds after the cast is accepted at which the hit resolves (the "release"). */
  atMs: number;
  base: number;
  /** Multiplier applied to Attack or Qi Attack depending on damageType. */
  scaling: number;
  shape: AreaShape;
  knockback?: number;
  statuses?: StatusApplication[];
}

export interface ComboStep {
  animation: string;
  vfx: string;
  sfx: string;
  hit: SkillHit;
  /** Total time the caster is locked (cast + recovery). */
  lockMs: number;
}

export interface ProjectileDef {
  speed: number;
  maxDistance: number;
  width: number;
  pierce: boolean;
}

export interface SkillDef {
  id: string;
  name: string;
  nameZh: string;
  sect: SectId | 'monster';
  element: Element | null;
  description: string;
  category: SkillCategory;
  damageType: DamageType;
  manaCost: number;
  staminaCost: number;
  cooldownMs: number;
  /** Wind-up before release (the first hit). */
  castTimeMs: number;
  /** Total time the caster is rooted, including wind-up and recovery. */
  lockMs: number;
  /** Maximum distance to the selected target when the cast starts. */
  range: number;
  target: 'enemy' | 'self' | 'direction';
  requiresTarget: boolean;
  hitbox: 'instant' | 'projectile';
  projectile?: ProjectileDef;
  hits: SkillHit[];
  /** Basic attacks cycle through combo steps; `hits` is ignored when present. */
  combo?: ComboStep[];
  /** Window after a combo step during which the next step chains (ms). */
  comboWindowMs?: number;
  levelRequired: number;
  maxLevel: number;
  upgrade: { basePerLevel: number; scalingPerLevel: number };
  weaponRequired: WeaponType | null;
  animation: string;
  vfx: string;
  sfx: string;
  icon: string;
  /** Server-side validation rules. */
  validation: {
    /** Extra metres tolerated on range checks (latency / target movement). */
    rangeTolerance: number;
  };
  /** Monster attacks only: show a ground warning for this long before release. */
  telegraphMs?: number;
}

const WUDANG_COMBO: ComboStep[] = [
  {
    animation: 'ATTACK_1',
    vfx: 'sword_arc_light',
    sfx: 'sword_swing',
    lockMs: 380,
    hit: { atMs: 170, base: 3, scaling: 1.0, shape: { kind: 'arc', radius: 2.7, halfAngleDeg: 60 } },
  },
  {
    animation: 'ATTACK_2',
    vfx: 'sword_arc_light',
    sfx: 'sword_swing',
    lockMs: 400,
    hit: { atMs: 180, base: 3, scaling: 1.1, shape: { kind: 'arc', radius: 2.7, halfAngleDeg: 60 } },
  },
  {
    animation: 'ATTACK_3',
    vfx: 'sword_arc_heavy',
    sfx: 'sword_swing_heavy',
    lockMs: 560,
    hit: {
      atMs: 260,
      base: 5,
      scaling: 1.5,
      shape: { kind: 'arc', radius: 3.0, halfAngleDeg: 80 },
      knockback: 0.8,
    },
  },
];

export const SKILLS: Readonly<Record<string, SkillDef>> = {
  wudang_sword_combo: {
    id: 'wudang_sword_combo',
    name: 'Flowing Cloud Swordplay',
    nameZh: '流云剑式',
    sect: 'wudang',
    element: 'earth',
    description:
      'A three-step sword sequence. The third cut sweeps wide and pushes enemies back. Chain the steps within a breath to complete the form.',
    category: 'basic',
    damageType: 'physical',
    manaCost: 0,
    staminaCost: 0,
    cooldownMs: 0,
    castTimeMs: 170,
    lockMs: 380,
    range: 2.7,
    target: 'direction',
    requiresTarget: false,
    hitbox: 'instant',
    hits: [],
    combo: WUDANG_COMBO,
    comboWindowMs: 700,
    levelRequired: 1,
    maxLevel: 10,
    upgrade: { basePerLevel: 1, scalingPerLevel: 0.03 },
    weaponRequired: 'sword',
    animation: 'ATTACK_COMBO',
    vfx: 'sword_arc_light',
    sfx: 'sword_swing',
    icon: 'combo',
    validation: { rangeTolerance: 0.6 },
  },
  wudang_cloud_parting_strike: {
    id: 'wudang_cloud_parting_strike',
    name: 'Cloud-Parting Strike',
    nameZh: '破云击',
    sect: 'wudang',
    element: 'earth',
    description:
      'Gathers earthen qi along the blade and drives a single decisive thrust through the target. Critical strikes stagger the foe.',
    category: 'single',
    damageType: 'physical',
    manaCost: 10,
    staminaCost: 0,
    cooldownMs: 4000,
    castTimeMs: 340,
    lockMs: 640,
    range: 3.4,
    target: 'enemy',
    requiresTarget: true,
    hitbox: 'instant',
    hits: [{ atMs: 340, base: 12, scaling: 1.8, shape: { kind: 'single' }, knockback: 0.5 }],
    levelRequired: 1,
    maxLevel: 10,
    upgrade: { basePerLevel: 4, scalingPerLevel: 0.08 },
    weaponRequired: 'sword',
    animation: 'CAST_THRUST',
    vfx: 'cloud_parting_strike',
    sfx: 'skill_thrust',
    icon: 'thrust',
    validation: { rangeTolerance: 1.0 },
  },
  wudang_sword_qi_crescent: {
    id: 'wudang_sword_qi_crescent',
    name: 'Sword Qi Crescent',
    nameZh: '剑气新月',
    sect: 'wudang',
    element: 'earth',
    description:
      'Releases a crescent of condensed sword qi that flies forward, cutting through every enemy in its path.',
    category: 'projectile',
    damageType: 'qi',
    manaCost: 16,
    staminaCost: 0,
    cooldownMs: 6000,
    castTimeMs: 380,
    lockMs: 720,
    range: 15,
    target: 'direction',
    requiresTarget: false,
    hitbox: 'projectile',
    projectile: { speed: 22, maxDistance: 15, width: 1.8, pierce: true },
    hits: [{ atMs: 380, base: 10, scaling: 1.4, shape: { kind: 'line', length: 15, width: 1.8 } }],
    levelRequired: 2,
    maxLevel: 10,
    upgrade: { basePerLevel: 3, scalingPerLevel: 0.07 },
    weaponRequired: 'sword',
    animation: 'CAST_SLASH_WAVE',
    vfx: 'sword_qi_crescent',
    sfx: 'skill_wave',
    icon: 'crescent',
    validation: { rangeTolerance: 0 },
  },
  wudang_taiji_formation: {
    id: 'wudang_taiji_formation',
    name: 'Taiji Formation',
    nameZh: '太极阵',
    sect: 'wudang',
    element: 'earth',
    description:
      'Traces a turning Taiji diagram on the ground. Its shockwave damages, throws back and slows every nearby enemy, interrupting their attacks.',
    category: 'control',
    damageType: 'qi',
    manaCost: 28,
    staminaCost: 0,
    cooldownMs: 12000,
    castTimeMs: 600,
    lockMs: 980,
    range: 5.5,
    target: 'self',
    requiresTarget: false,
    hitbox: 'instant',
    hits: [
      {
        atMs: 600,
        base: 14,
        scaling: 1.25,
        shape: { kind: 'circle', radius: 5.5 },
        knockback: 2.6,
        statuses: [{ status: 'slow', chance: 1, durationMs: 3000, magnitude: 0.4 }],
      },
    ],
    levelRequired: 3,
    maxLevel: 10,
    upgrade: { basePerLevel: 3, scalingPerLevel: 0.06 },
    weaponRequired: 'sword',
    animation: 'CAST_FORMATION',
    vfx: 'taiji_formation',
    sfx: 'skill_formation',
    icon: 'taiji',
    validation: { rangeTolerance: 0.5 },
  },

  // ---------------------------------------------------------------- monster attacks
  wolf_bite: {
    id: 'wolf_bite',
    name: 'Bite',
    nameZh: '撕咬',
    sect: 'monster',
    element: 'wood',
    description: 'A snapping bite.',
    category: 'single',
    damageType: 'physical',
    manaCost: 0,
    staminaCost: 0,
    cooldownMs: 1600,
    castTimeMs: 380,
    lockMs: 700,
    range: 1.9,
    target: 'enemy',
    requiresTarget: true,
    hitbox: 'instant',
    hits: [{ atMs: 380, base: 5, scaling: 1.0, shape: { kind: 'single' } }],
    levelRequired: 1,
    maxLevel: 1,
    upgrade: { basePerLevel: 0, scalingPerLevel: 0 },
    weaponRequired: null,
    animation: 'ATTACK_1',
    vfx: 'claw_swipe',
    sfx: 'wolf_bite',
    icon: 'bite',
    validation: { rangeTolerance: 0.6 },
  },
  bandit_slash: {
    id: 'bandit_slash',
    name: 'Saber Slash',
    nameZh: '劈砍',
    sect: 'monster',
    element: 'metal',
    description: 'A rough diagonal saber cut.',
    category: 'single',
    damageType: 'physical',
    manaCost: 0,
    staminaCost: 0,
    cooldownMs: 1900,
    castTimeMs: 450,
    lockMs: 850,
    range: 2.3,
    target: 'enemy',
    requiresTarget: true,
    hitbox: 'instant',
    hits: [{ atMs: 450, base: 6, scaling: 1.0, shape: { kind: 'single' } }],
    levelRequired: 1,
    maxLevel: 1,
    upgrade: { basePerLevel: 0, scalingPerLevel: 0 },
    weaponRequired: null,
    animation: 'ATTACK_1',
    vfx: 'saber_arc',
    sfx: 'sword_swing',
    icon: 'slash',
    validation: { rangeTolerance: 0.6 },
  },
  bandit_heavy_chop: {
    id: 'bandit_heavy_chop',
    name: 'Mountain-Splitting Chop',
    nameZh: '开山劈',
    sect: 'monster',
    element: 'metal',
    description: 'A telegraphed two-handed chop that stuns anyone caught in front of the bandit. Dodge it!',
    category: 'area',
    damageType: 'physical',
    manaCost: 0,
    staminaCost: 0,
    cooldownMs: 8000,
    castTimeMs: 1000,
    lockMs: 1500,
    range: 3.2,
    target: 'enemy',
    requiresTarget: true,
    hitbox: 'instant',
    hits: [
      {
        atMs: 1000,
        base: 10,
        scaling: 1.9,
        shape: { kind: 'arc', radius: 3.4, halfAngleDeg: 40 },
        statuses: [{ status: 'stun', chance: 1, durationMs: 900, magnitude: 1 }],
      },
    ],
    levelRequired: 1,
    maxLevel: 1,
    upgrade: { basePerLevel: 0, scalingPerLevel: 0 },
    weaponRequired: null,
    animation: 'ATTACK_HEAVY',
    vfx: 'heavy_chop',
    sfx: 'heavy_impact',
    icon: 'chop',
    validation: { rangeTolerance: 0.4 },
    telegraphMs: 1000,
  },
};

export function getSkill(id: string): SkillDef | undefined {
  return SKILLS[id];
}

/** Hits for a given combo step (or the skill's own hits when it is not a combo skill). */
export function skillStepHits(skill: SkillDef, comboStep: number): SkillHit[] {
  if (skill.combo && skill.combo.length > 0) {
    const step = skill.combo[comboStep % skill.combo.length] as ComboStep;
    return [step.hit];
  }
  return skill.hits;
}

export function skillStepLockMs(skill: SkillDef, comboStep: number): number {
  if (skill.combo && skill.combo.length > 0) {
    return (skill.combo[comboStep % skill.combo.length] as ComboStep).lockMs;
  }
  return skill.lockMs;
}

export function skillStepCastMs(skill: SkillDef, comboStep: number): number {
  const hits = skillStepHits(skill, comboStep);
  return hits[0]?.atMs ?? skill.castTimeMs;
}
